<template>
	<div ref="nodeContainer" class="layout-container">
		<transition name="van-fade">
			<template v-if="isLoading"></template>
			<CFullEmpty v-else-if="isError" :style="{ height: h100 }" />
			<template v-else>
				<SmartLayoutC
					v-if="supportedTemplates.includes(currentLayoutType)"
					compId="layout_wrapper"
					mode="basic"
				/>
				<div v-else>{{ unSupportedValue }}</div>
			</template>
		</transition>
	</div>
</template>

<script>
export default {
	name: 'DynamicLayout'
};
</script>
<script setup>
import { computed, provide, onMounted, onBeforeUnmount, ref, getCurrentInstance, watch } from 'vue';
import SmartLayoutC from '@rebuild/components/SmartLayoutC.vue';
import CFullEmpty from '@/view/enterprise/components/c-full-empty.vue';
import { useNavigation } from '@rebuild/hooks/useNavigation';
import { useLangListeners } from '@rebuild/hooks/useLangListeners';
import { useCoreData, useFuncStep } from '@rebuild/hooks/data/useCoreData';
import { useStore, useRoute } from '@/utils/vue-api';
import { changeLocaleLang } from '@/assets/js/i18n';
import { useCommonData } from '@rebuild/hooks/useCommonData';
import { get100vh } from '@/utils/api';
import { useIframeMessage } from '@rebuild/hooks/message';
import { useEventIntercept } from '@rebuild/hooks/useEventIntercept';
import { TEMPLATE_MAP } from '@/store/modules/irstore';

// -------------- 派发模板类型
const { currentTemplate } = useNavigation();
const currentLayoutType = computed(() => currentTemplate.value);
provide('TEMPLATE_TYPE', currentLayoutType);

const store = useStore();
const route = useRoute();
const vm = getCurrentInstance();

const h100 = get100vh();

const {
	isLoading,
	showLoading,
	hideLoading,

	loadIRBasicInfo,
	loadIRPageData,
	loadUIConfigWithTmplId
} = useCoreData();

const getCardShareData = () => {
	const { logo } = store.getters.getIrBaseInfo;
	return {
		title: store.getters.getIrPageTitle,
		link: location.href,
		img_url: logo,
		desc: ''
	};
};
const { isYiliMiniProgram } = useCommonData();
const getFlowShareData = () => {
	const { name, logo, slogan } = store.getters.getIrBaseInfo;
	const bridge = vm.proxy.$bridge;
	let _shareTitle = bridge.shareTitle || `${name}企业号`;
	if (isYiliMiniProgram.value) {
		_shareTitle = '伊利股份投资者关系';
	}
	return Object.assign({}, bridge.getShareData(), {
		img_url: logo,
		link: location.href,
		title: _shareTitle,
		desc: slogan ? slogan : `关注${name}企业号`
	});
};
const updateWxShare = () => {
	if (currentLayoutType.value === 'flow') {
		shareData = getFlowShareData();
	} else {
		shareData = getCardShareData();
	}
	// 调用微信端分享方法
	store.dispatch('wxShare', shareData);
	window.shareData = shareData;
};
watch(
	() => vm.proxy.$route,
	newRoute => {
		updateWxShare();
		updatePageTitle(newRoute);
	}
);

const initPageTitle = () => {
	setTimeout(() => {
		const name = getHomeTitle();
		if (name) document.title = name;
	}, 200);
};
const getHomeTitle = () => {
	let name;
	if (currentLayoutType.value === 'flow') {
		name = store.getters.getIrName;
		const { code, market } = store.getters.getIrBaseInfo;
		if (code && market) {
			name += ` ${code}.${market.toUpperCase()}`;
		}
	} else {
		name = store.getters.getIrPageTitle;
	}
	return name;
};
const getModuleTitle = newRoute => {
	const moduleId = newRoute.params.moduleId;
	const globalModules = store.getters.getIrGlobalModules;
	const systemModules = store.getters.getIrTemplateFlattenModules;
	const allModules = [...systemModules, ...globalModules];
	const findModule = allModules.find(item => item.moduleId === moduleId);
	return findModule?.name || null;
};
const updatePageTitle = newRoute => {
	const isHomeView = newRoute.name.includes('home');
	setTimeout(() => {
		const name = isHomeView ? getHomeTitle() : getModuleTitle(newRoute);
		if (name) document.title = name;
	}, 200);
};

const isError = ref(false);
const { runSteps, wrapStep } = useFuncStep();

const { viewInIframe, viewInPreview } = useCommonData();

onMounted(async () => {
	const forcePageType = route.query.pageType;
	store.commit('setActiveTemplate', forcePageType || TEMPLATE_MAP[currentLayoutType.value]);

	// 编辑预览
	if (viewInIframe.value || viewInPreview.value) {
		sessionStorage.setItem('ir_from_preview', true);
	} else {
		sessionStorage.removeItem('ir_from_preview');
	}

	// 三方（小程序）渠道进入首页
	const { comein_channel } = route.query;
	if (comein_channel) {
		localStorage.setItem('channel', comein_channel);
	} else {
		localStorage.removeItem('channel');
	}

	showLoading();
	const { hasError } = await runSteps([
		wrapStep(loadIRBasicInfo),
		wrapStep(loadUIConfigWithTmplId),
		wrapStep(loadIRPageData)
	]);
	if (hasError) {
		isError.value = true;
	}
	hideLoading();

	initPageTitle();
	updateWxShare();
});

const supportedTemplates = Object.keys(TEMPLATE_MAP);
const unSupportedValue = '<Unknown>';

const forceRefreshLang = () => {
	// 同步覆盖语言缓存
	if (route.query.lang) changeLocaleLang(route.query.lang);
};
forceRefreshLang();

// 语言切换监听 & 重定向控制
useLangListeners();

const { addIframeMessageListener, removeIframeMessageListener } = useIframeMessage();

const { nodeContainer, addEventIntercept, removeEventIntercept } = useEventIntercept();

onMounted(() => {
	addIframeMessageListener();
	addEventIntercept();
});
onBeforeUnmount(() => {
	removeIframeMessageListener();
	removeEventIntercept();
});
</script>
